import React, { useState, useRef } from 'react';
import './DominoGenerator.css';

interface Domino {
  id: string;
  leftDots: number;
  rightDots: number;
  x: number;
  y: number;
  rotation: number; // 0, 90, 180, 270 degrees
}

const DominoGenerator: React.FC = () => {
  const [dominoes, setDominoes] = useState<Domino[]>([]);
  const [history, setHistory] = useState<Domino[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [selectedDomino, setSelectedDomino] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{x: number, y: number}>({x: 0, y: 0});
  const [inputLeft, setInputLeft] = useState<string>('');
  const [inputRight, setInputRight] = useState<string>('');
  const [isDraggingFromPalette, setIsDraggingFromPalette] = useState(false);
  const [draggedPaletteDomino, setDraggedPaletteDomino] = useState<{leftDots: number, rightDots: number} | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  
  const GRID_SIZE = 30;
  const CANVAS_WIDTH = 900;
  const CANVAS_HEIGHT = 600;
  const DOMINO_WIDTH = GRID_SIZE * 4; // 4 grid units wide
  const DOMINO_HEIGHT = GRID_SIZE * 2; // 2 grid units tall

  // Snap coordinate to grid
  const snapToGrid = (coord: number) => {
    return Math.round(coord / GRID_SIZE) * GRID_SIZE;
  };

  // Get mouse coordinates relative to SVG
  const getMouseCoords = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    
    const rect = svg.getBoundingClientRect();
    const x = snapToGrid(event.clientX - rect.left);
    const y = snapToGrid(event.clientY - rect.top);
    return { x, y };
  };

  // Add to history for undo functionality
  const addToHistory = (newDominoes: Domino[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newDominoes]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setDominoes(newDominoes);
  };

  // Undo functionality
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setDominoes([...history[newIndex]]);
    }
  };

  // Add a new domino
  const addDomino = () => {
    const left = parseInt(inputLeft);
    const right = parseInt(inputRight);
    
    if (isNaN(left) || isNaN(right) || left < 0 || left > 6 || right < 0 || right > 6) {
      alert('Please enter valid numbers between 0 and 6');
      return;
    }

    const newDomino: Domino = {
      id: `domino-${Date.now()}`,
      leftDots: left,
      rightDots: right,
      x: GRID_SIZE * 2,
      y: GRID_SIZE * 2,
      rotation: 0
    };

    const newDominoes = [...dominoes, newDomino];
    addToHistory(newDominoes);
    setInputLeft('');
    setInputRight('');
  };

  // Rotate a domino
  const rotateDomino = (dominoId: string) => {
    const newDominoes = dominoes.map(domino => {
      if (domino.id === dominoId) {
        return { ...domino, rotation: (domino.rotation + 90) % 360 };
      }
      return domino;
    });
    addToHistory(newDominoes);
  };

  // Clear all dominoes
  const clearAll = () => {
    addToHistory([]);
  };

  // Generate all possible domino combinations grouped by left dots (0-0 to 6-6)
  const getAllDominoCombinationsGrouped = () => {
    const groups = [];
    for (let i = 0; i <= 6; i++) {
      const group = [];
      for (let j = i; j <= 6; j++) {
        group.push({ leftDots: i, rightDots: j });
      }
      groups.push({ leftDots: i, dominoes: group });
    }
    return groups;
  };

  // Remove a domino
  const removeDomino = (dominoId: string) => {
    const newDominoes = dominoes.filter(domino => domino.id !== dominoId);
    addToHistory(newDominoes);
  };

  // Handle mouse down on domino
  const handleDominoMouseDown = (event: React.MouseEvent<SVGRectElement>, dominoId: string) => {
    event.stopPropagation();
    
    // Check for Ctrl+click to remove domino
    if (event.ctrlKey || event.metaKey) {
      removeDomino(dominoId);
      return;
    }

    const domino = dominoes.find(d => d.id === dominoId);
    if (!domino) return;

    // Create a synthetic event for the SVG element
    const svgEvent = {
      ...event,
      currentTarget: svgRef.current!,
      target: svgRef.current!
    } as React.MouseEvent<SVGSVGElement>;

    const { x, y } = getMouseCoords(svgEvent);
    setSelectedDomino(dominoId);
    setIsDragging(true);
    setDragOffset({
      x: x - domino.x,
      y: y - domino.y
    });
  };

  // Handle palette domino click/drag
  const handlePaletteDominoClick = (leftDots: number, rightDots: number) => {
    const newDomino: Domino = {
      id: `domino-${Date.now()}`,
      leftDots,
      rightDots,
      x: GRID_SIZE * 2,
      y: GRID_SIZE * 2,
      rotation: 0
    };

    const newDominoes = [...dominoes, newDomino];
    addToHistory(newDominoes);
  };

  // Handle mouse down on palette domino
  const handlePaletteDominoMouseDown = (event: React.MouseEvent, leftDots: number, rightDots: number) => {
    event.preventDefault();
    setIsDraggingFromPalette(true);
    setDraggedPaletteDomino({ leftDots, rightDots });
  };

  // Handle mouse move
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging && selectedDomino) {
      const { x, y } = getMouseCoords(event);
      const newDominoes = dominoes.map(domino => {
        if (domino.id === selectedDomino) {
          return {
            ...domino,
            x: x - dragOffset.x,
            y: y - dragOffset.y
          };
        }
        return domino;
      });
      setDominoes(newDominoes);
    }
  };

  // Handle mouse up
  const handleMouseUp = (event: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging && selectedDomino) {
      // Save the final position to history
      addToHistory(dominoes);
    }
    
    if (isDraggingFromPalette && draggedPaletteDomino) {
      // Drop domino from palette onto grid
      const { x, y } = getMouseCoords(event);
      const newDomino: Domino = {
        id: `domino-${Date.now()}`,
        leftDots: draggedPaletteDomino.leftDots,
        rightDots: draggedPaletteDomino.rightDots,
        x,
        y,
        rotation: 0
      };

      const newDominoes = [...dominoes, newDomino];
      addToHistory(newDominoes);
    }
    
    setIsDragging(false);
    setSelectedDomino(null);
    setDragOffset({x: 0, y: 0});
    setIsDraggingFromPalette(false);
    setDraggedPaletteDomino(null);
  };

  // Handle double click to rotate
  const handleDominoDoubleClick = (dominoId: string) => {
    rotateDomino(dominoId);
  };

  // Render domino dots pattern
  const renderDots = (count: number, x: number, y: number, width: number, height: number): React.ReactElement[] => {
    const dots: React.ReactElement[] = [];
    const dotRadius = 3;
    const margin = 8;
    
    // Define dot positions for each number (0-6)
    const dotPatterns: {[key: number]: {x: number, y: number}[]} = {
      0: [],
      1: [{x: 0.5, y: 0.5}],
      2: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.75}],
      3: [{x: 0.25, y: 0.25}, {x: 0.5, y: 0.5}, {x: 0.75, y: 0.75}],
      4: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.25}, {x: 0.25, y: 0.75}, {x: 0.75, y: 0.75}],
      5: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.25}, {x: 0.5, y: 0.5}, {x: 0.25, y: 0.75}, {x: 0.75, y: 0.75}],
      6: [{x: 0.25, y: 0.2}, {x: 0.75, y: 0.2}, {x: 0.25, y: 0.5}, {x: 0.75, y: 0.5}, {x: 0.25, y: 0.8}, {x: 0.75, y: 0.8}]
    };

    const pattern = dotPatterns[count] || [];
    
    pattern.forEach((dot, index) => {
      const dotX = x + margin + (width - 2 * margin) * dot.x;
      const dotY = y + margin + (height - 2 * margin) * dot.y;
      
      dots.push(
        <circle
          key={index}
          cx={dotX}
          cy={dotY}
          r={dotRadius}
          fill="#000000"
        />
      );
    });

    return dots;
  };

  // Render a single domino
  const renderDomino = (domino: Domino) => {
    const isHorizontal = domino.rotation === 0 || domino.rotation === 180;
    const width = isHorizontal ? DOMINO_WIDTH : DOMINO_HEIGHT;
    const height = isHorizontal ? DOMINO_HEIGHT : DOMINO_WIDTH;
    
    // Adjust dots based on rotation
    let leftDots = domino.leftDots;
    let rightDots = domino.rightDots;
    
    if (domino.rotation === 180) {
      [leftDots, rightDots] = [rightDots, leftDots];
    }

    return (
      <g key={domino.id}>
        {/* Domino background */}
        <rect
          x={domino.x}
          y={domino.y}
          width={width}
          height={height}
          fill="#f5f5f5"
          stroke="#333333"
          strokeWidth="2"
          rx="8"
          ry="8"
          style={{ cursor: 'pointer' }}
          onMouseDown={(e) => handleDominoMouseDown(e, domino.id)}
          onDoubleClick={() => handleDominoDoubleClick(domino.id)}
        />
        
        {/* Center divider */}
        {isHorizontal ? (
          <line
            x1={domino.x + width / 2}
            y1={domino.y}
            x2={domino.x + width / 2}
            y2={domino.y + height}
            stroke="#333333"
            strokeWidth="2"
          />
        ) : (
          <line
            x1={domino.x}
            y1={domino.y + height / 2}
            x2={domino.x + width}
            y2={domino.y + height / 2}
            stroke="#333333"
            strokeWidth="2"
          />
        )}
        
        {/* Left/Top side dots */}
        {isHorizontal ? 
          renderDots(leftDots, domino.x, domino.y, width / 2, height) :
          renderDots(domino.rotation === 90 ? rightDots : leftDots, domino.x, domino.y, width, height / 2)
        }
        
        {/* Right/Bottom side dots */}
        {isHorizontal ? 
          renderDots(rightDots, domino.x + width / 2, domino.y, width / 2, height) :
          renderDots(domino.rotation === 90 ? leftDots : rightDots, domino.x, domino.y + height / 2, width, height / 2)
        }
      </g>
    );
  };

  // Render grid dots
  const renderGridDots = () => {
    const dots = [];
    for (let x = 0; x <= CANVAS_WIDTH; x += GRID_SIZE) {
      for (let y = 0; y <= CANVAS_HEIGHT; y += GRID_SIZE) {
        dots.push(
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r="1"
            fill="#d0d0d0"
          />
        );
      }
    }
    return dots;
  };

  // Render palette domino
  const renderPaletteDomino = (leftDots: number, rightDots: number) => {
    const paletteWidth = 60;
    const paletteHeight = 30;

    return (
      <div
        key={`${leftDots}-${rightDots}`}
        className="palette-domino"
        onClick={() => handlePaletteDominoClick(leftDots, rightDots)}
        onMouseDown={(e) => handlePaletteDominoMouseDown(e, leftDots, rightDots)}
      >
        <svg width={paletteWidth} height={paletteHeight}>
          {/* Domino background */}
          <rect
            x="1"
            y="1"
            width={paletteWidth - 2}
            height={paletteHeight - 2}
            fill="#f5f5f5"
            stroke="#333333"
            strokeWidth="1"
            rx="2"
          />
          
          {/* Center divider */}
          <line
            x1={paletteWidth / 2}
            y1="1"
            x2={paletteWidth / 2}
            y2={paletteHeight - 1}
            stroke="#333333"
            strokeWidth="1"
          />
          
          {/* Left side dots */}
          {renderDots(leftDots, 1, 1, paletteWidth / 2 - 1, paletteHeight - 2)}
          
          {/* Right side dots */}
          {renderDots(rightDots, paletteWidth / 2, 1, paletteWidth / 2 - 1, paletteHeight - 2)}
        </svg>
        
        {/* Label */}
        <div className="domino-label">
          {leftDots}-{rightDots}
        </div>
      </div>
    );
  };

  // Export to PNG
  const exportToPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Find the bounding box of all dominoes
    let minX = CANVAS_WIDTH;
    let minY = CANVAS_HEIGHT;
    let maxX = 0;
    let maxY = 0;

    dominoes.forEach(domino => {
      const isHorizontal = domino.rotation === 0 || domino.rotation === 180;
      const width = isHorizontal ? DOMINO_WIDTH : DOMINO_HEIGHT;
      const height = isHorizontal ? DOMINO_HEIGHT : DOMINO_WIDTH;
      
      minX = Math.min(minX, domino.x);
      minY = Math.min(minY, domino.y);
      maxX = Math.max(maxX, domino.x + width);
      maxY = Math.max(maxY, domino.y + height);
    });

    // Add equal margin on all sides
    const margin = 40;
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    
    // Calculate export dimensions with equal margins
    const exportWidth = contentWidth + (2 * margin);
    const exportHeight = contentHeight + (2 * margin);
    
    // Calculate centering offsets
    const offsetX = minX - margin;
    const offsetY = minY - margin;

    canvas.width = exportWidth;
    canvas.height = exportHeight;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportWidth, exportHeight);

    // Translate to account for cropping and centering
    ctx.save();
    ctx.translate(-offsetX, -offsetY);

    // Helper function to draw rounded rectangle
    const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };

    // Draw dominoes
    dominoes.forEach(domino => {
      const isHorizontal = domino.rotation === 0 || domino.rotation === 180;
      const width = isHorizontal ? DOMINO_WIDTH : DOMINO_HEIGHT;
      const height = isHorizontal ? DOMINO_HEIGHT : DOMINO_WIDTH;
      
      // Draw domino background with rounded corners
      ctx.fillStyle = '#f5f5f5';
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 2;
      
      drawRoundedRect(domino.x, domino.y, width, height, 8);
      ctx.fill();
      ctx.stroke();
      
      // Draw center divider
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(domino.x + width / 2, domino.y);
        ctx.lineTo(domino.x + width / 2, domino.y + height);
      } else {
        ctx.moveTo(domino.x, domino.y + height / 2);
        ctx.lineTo(domino.x + width, domino.y + height / 2);
      }
      ctx.stroke();
      
      // Draw dots (simplified for canvas)
      ctx.fillStyle = '#000000';
      const drawCanvasDots = (count: number, x: number, y: number, w: number, h: number) => {
        const dotRadius = 3;
        const margin = 8;
        
        const dotPatterns: {[key: number]: {x: number, y: number}[]} = {
          0: [],
          1: [{x: 0.5, y: 0.5}],
          2: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.75}],
          3: [{x: 0.25, y: 0.25}, {x: 0.5, y: 0.5}, {x: 0.75, y: 0.75}],
          4: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.25}, {x: 0.25, y: 0.75}, {x: 0.75, y: 0.75}],
          5: [{x: 0.25, y: 0.25}, {x: 0.75, y: 0.25}, {x: 0.5, y: 0.5}, {x: 0.25, y: 0.75}, {x: 0.75, y: 0.75}],
          6: [{x: 0.25, y: 0.2}, {x: 0.75, y: 0.2}, {x: 0.25, y: 0.5}, {x: 0.75, y: 0.5}, {x: 0.25, y: 0.8}, {x: 0.75, y: 0.8}]
        };

        const pattern = dotPatterns[count] || [];
        
        pattern.forEach(dot => {
          const dotX = x + margin + (w - 2 * margin) * dot.x;
          const dotY = y + margin + (h - 2 * margin) * dot.y;
          
          ctx.beginPath();
          ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
          ctx.fill();
        });
      };
      
      // Adjust dots based on rotation
      let leftDots = domino.leftDots;
      let rightDots = domino.rightDots;
      
      if (domino.rotation === 180) {
        [leftDots, rightDots] = [rightDots, leftDots];
      }
      
      // Draw dots
      if (isHorizontal) {
        drawCanvasDots(leftDots, domino.x, domino.y, width / 2, height);
        drawCanvasDots(rightDots, domino.x + width / 2, domino.y, width / 2, height);
      } else {
        drawCanvasDots(domino.rotation === 90 ? rightDots : leftDots, domino.x, domino.y, width, height / 2);
        drawCanvasDots(domino.rotation === 90 ? leftDots : rightDots, domino.x, domino.y + height / 2, width, height / 2);
      }
    });

    ctx.restore();

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `domino-layout-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="domino-generator">
      <h2>Domino Generator</h2>
      
      <div className="controls">
        <div className="domino-input">
          <label>Add Domino:</label>
          <input
            type="number"
            min="0"
            max="6"
            value={inputLeft}
            onChange={(e) => setInputLeft(e.target.value)}
            placeholder="Left (0-6)"
            className="dots-input"
          />
          <span>-</span>
          <input
            type="number"
            min="0"
            max="6"
            value={inputRight}
            onChange={(e) => setInputRight(e.target.value)}
            placeholder="Right (0-6)"
            className="dots-input"
          />
          <button onClick={addDomino} className="add-btn">
            Add Domino
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            onClick={undo} 
            className="control-btn"
            disabled={historyIndex <= 0}
          >
            Undo
          </button>
          <button onClick={clearAll} className="control-btn">
            Clear All
          </button>
          <button onClick={exportToPNG} className="export-btn">
            Export PNG
          </button>
        </div>
      </div>

      <div className="instructions">
        <p><strong>Instructions:</strong></p>
        <p>• Click dominoes from the palette on the left to add them to the grid</p>
        <p>• Drag dominoes to move them around the grid</p>
        <p>• Double-click a domino to rotate it 90 degrees</p>
        <p>• Hold Ctrl and click a domino to remove it</p>
        <p>• Use Undo to revert last action</p>
      </div>
      
      <div className="main-layout">
        {/* Drawing Area */}
        <div className="drawing-area">
          <svg
            ref={svgRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="drawing-surface"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Grid dots */}
            {renderGridDots()}
            
            {/* Dominoes */}
            {dominoes.map(domino => renderDomino(domino))}
          </svg>
        </div>

        {/* Domino Palette */}
        <div className="domino-palette">
          <h3>Domino Palette - Click or drag to add dominoes</h3>
          <div className="palette-container">
            {getAllDominoCombinationsGrouped().map((group) => (
              <div key={group.leftDots} className="domino-group">
                <div className="group-header">{group.leftDots}</div>
                <div className="group-dominoes">
                  {group.dominoes.map((domino) => 
                    renderPaletteDomino(domino.leftDots, domino.rightDots)
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hidden canvas for export */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default DominoGenerator;